import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout/Layout";
import "../styles/PaymentModern.css";

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const accessToken = searchParams.get("token");

  const checkStatus = async () => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`/api/v1/payments/status/${accessToken}`);
      if (data?.success) {
        setPayment(data.payment);
        if (["completed", "failed", "expired", "refunded"].includes(data.payment.paymentStatus)) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
    pollRef.current = setInterval(checkStatus, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [accessToken]); // eslint-disable-line

  const formatPrice = (price) => (price || 0).toLocaleString("ar-IQ") + " د.ع";

  if (loading) {
    return (
      <Layout title="نتيجة الدفع - النخبة ستور">
        <div className="pm-page">
          <div className="pm-container">
            <div className="pm-loading">
              <div className="pm-spinner" />
              <p>جاري التحقق من حالة الدفع...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!accessToken || !payment) {
    return (
      <Layout title="نتيجة الدفع - النخبة ستور">
        <div className="pm-page">
          <div className="pm-container">
            <div className="pm-status-card pm-status-error">
              <div className="pm-status-icon-wrap pm-icon-error">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              </div>
              <h3>لا توجد بيانات دفع</h3>
              <p>الرابط غير صالح أو منتهي الصلاحية</p>
              <button className="pm-btn pm-btn-primary" onClick={() => navigate("/")}>
                العودة للرئيسية
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const isSuccess = payment.paymentStatus === "completed";
  const isPending = payment.paymentStatus === "pending" || payment.paymentStatus === "processing";
  const isFailed = payment.paymentStatus === "failed" || payment.paymentStatus === "expired";

  return (
    <Layout title="نتيجة الدفع - النخبة ستور">
      <div className="pm-page">
        <div className="pm-container">

          {/* Success State */}
          {isSuccess && (
            <div className="pm-result-wrap animate-fade-in">
              <div className="pm-status-card pm-status-success">
                <div className="pm-status-icon-wrap pm-icon-success">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                </div>
                <h2>تم الدفع بنجاح!</h2>
                <p className="pm-result-sub">تمت معالجة عملية الدفع بنجاح</p>
              </div>

              <div className="pm-card">
                <div className="pm-card-header">
                  <h4>تفاصيل العملية</h4>
                  <span className="pm-status-chip pm-chip-success">مدفوع</span>
                </div>

                <div className="pm-receipt-grid">
                  <div className="pm-receipt-item">
                    <span className="pm-receipt-label">رقم الفاتورة</span>
                    <span className="pm-receipt-value">{payment.invoiceNumber}</span>
                  </div>
                  <div className="pm-receipt-item">
                    <span className="pm-receipt-label">المبلغ</span>
                    <span className="pm-receipt-value pm-receipt-amount">{formatPrice(payment.totalAmount)}</span>
                  </div>
                  {payment.qi?.details?.maskedPan && (
                    <div className="pm-receipt-item">
                      <span className="pm-receipt-label">البطاقة</span>
                      <span className="pm-receipt-value pm-receipt-mono">{payment.qi.details.maskedPan}</span>
                    </div>
                  )}
                  {payment.qi?.details?.paymentSystem && (
                    <div className="pm-receipt-item">
                      <span className="pm-receipt-label">نوع البطاقة</span>
                      <span className="pm-receipt-value">{payment.qi.details.paymentSystem}</span>
                    </div>
                  )}
                  <div className="pm-receipt-item">
                    <span className="pm-receipt-label">طريقة الدفع</span>
                    <span className="pm-receipt-value">{payment.paymentMethod === "qi" ? "Qi Card" : "عند الاستلام"}</span>
                  </div>
                  <div className="pm-receipt-item">
                    <span className="pm-receipt-label">العميل</span>
                    <span className="pm-receipt-value">{payment.customer?.name}</span>
                  </div>
                </div>
              </div>

              <div className="pm-actions">
                <button className="pm-btn pm-btn-primary" onClick={() => navigate(`/invoice/${payment.accessToken}`)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  <span>عرض الفاتورة</span>
                </button>
                <button className="pm-btn pm-btn-outline" onClick={() => navigate("/")}>
                  <span>متابعة التسوق</span>
                </button>
              </div>
            </div>
          )}

          {/* Pending/Processing State */}
          {isPending && (
            <div className="pm-result-wrap animate-fade-in">
              <div className="pm-status-card pm-status-processing">
                <div className="pm-status-icon-wrap pm-icon-processing">
                  <div className="pm-pulse-ring" />
                  <div className="pm-spinner-large" />
                </div>
                <h2>جاري معالجة الدفع</h2>
                <p className="pm-result-sub">يرجى الانتظار، سيتم تحديث الحالة تلقائياً</p>
              </div>

              <div className="pm-card">
                <div className="pm-receipt-grid">
                  <div className="pm-receipt-item">
                    <span className="pm-receipt-label">رقم الفاتورة</span>
                    <span className="pm-receipt-value">{payment.invoiceNumber}</span>
                  </div>
                  <div className="pm-receipt-item">
                    <span className="pm-receipt-label">المبلغ</span>
                    <span className="pm-receipt-value pm-receipt-amount">{formatPrice(payment.totalAmount)}</span>
                  </div>
                </div>

                <div className="pm-poll-indicator">
                  <div className="pm-poll-dot" />
                  <span>يتم التحقق تلقائياً كل 5 ثوانٍ</span>
                </div>
              </div>
            </div>
          )}

          {/* Failed State */}
          {isFailed && (
            <div className="pm-result-wrap animate-fade-in">
              <div className="pm-status-card pm-status-failed">
                <div className="pm-status-icon-wrap pm-icon-error">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                </div>
                <h2>فشلت عملية الدفع</h2>
                <p className="pm-result-sub">لم تتم عملية الدفع. يمكنك المحاولة مرة أخرى أو اختيار الدفع عند الاستلام</p>
              </div>

              <div className="pm-card">
                <div className="pm-receipt-grid">
                  <div className="pm-receipt-item">
                    <span className="pm-receipt-label">رقم الفاتورة</span>
                    <span className="pm-receipt-value">{payment.invoiceNumber}</span>
                  </div>
                  <div className="pm-receipt-item">
                    <span className="pm-receipt-label">المبلغ</span>
                    <span className="pm-receipt-value pm-receipt-amount">{formatPrice(payment.totalAmount)}</span>
                  </div>
                </div>
              </div>

              <div className="pm-actions">
                {payment.accessToken && (
                  <button className="pm-btn pm-btn-pay" onClick={() => navigate(`/pay/${payment.accessToken}`)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                    <span>إعادة المحاولة</span>
                  </button>
                )}
                <button className="pm-btn pm-btn-outline" onClick={() => navigate("/")}>
                  <span>العودة للرئيسية</span>
                </button>
              </div>
            </div>
          )}

          {/* Security Footer */}
          <div className="pm-footer">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <span>معالجة آمنة عبر بوابة Qi Card</span>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default PaymentResult;
